export const BRAND_NAVY = '#455573'
export const BRAND_NAVY_LIGHT = '#6d7fa2'
export const BRAND_NAVY_DARK = '#242e3e'
export const BRAND_RED = '#D2212C'
export const CREAM = '#F4F1EB'
export const PAPER = '#ffffff'
export const PAGE_BG = '#f5f5f5'
export const TEXT_SECONDARY = '#546e7a'
export const TOUCH_MIN = 44
export const BOTTOM_NAV_HEIGHT = 56
export const CHROME_GAP = 12
/** Compact chip scale. Keeps status/metadata chips small and single-line on phone. */
export const CHIP_HEIGHT = 20
export const CHIP_HEIGHT_DENSE = 18
export const CHIP_FONT = '0.68rem'
export const CHIP_MAX_WIDTH = 140
export const CHIP_LABEL_PX = 0.625
/** Phone app bar — keep at touch-friendly height. */
export const APPBAR_HEIGHT = 56
/** Desktop inset chrome — slightly tighter than phone. */
export const APPBAR_HEIGHT_DESKTOP = 48
export const DRAWER_COMFORTABLE = 280
export const DRAWER_COMPACT = 58
export const CHROME_RADIUS = 12
/** Desktop nav row height (phone keeps TOUCH_MIN via list buttons). */
export const NAV_ITEM_HEIGHT_DESKTOP = 36
export const NAV_ITEM_HEIGHT_DESKTOP_CHILD = 32
/** Desktop icon button hit area inside chrome (phone stays TOUCH_MIN). */
export const CHROME_ICON_HIT = 36
/** Phone header search — compact field, not a flush slab. */
export const SEARCH_TRIGGER_RADIUS_MOBILE = 1
/** Desktop header search pill (MUI spacing units: 5 × 8px). */
export const SEARCH_TRIGGER_RADIUS_DESKTOP = 5
/** iPhone 16 portrait insets. Used when env() is 0 on fullscreen overlays. */
export const IOS_SAFE_TOP_FALLBACK_PX = 59
export const IOS_SAFE_BOTTOM_FALLBACK_PX = 34

/**
 * Top/bottom safe area.
 * Standalone PWA uses `env(safe-area-inset-*)`, then Playwright `--sat`/`--sab`,
 * then the iOS floor from `iosKeyboard.ts`.
 * Mobile Safari sets `--app-safe-*` to 0 so that inset does not stack on the
 * URL bar and bottom toolbar.
 */
export const SAFE_AREA_TOP =
  'max(var(--app-safe-top, env(safe-area-inset-top, 0px)), var(--sat, 0px), var(--app-sat-min, 0px))'
export const SAFE_AREA_BOTTOM =
  'max(var(--app-safe-bottom, env(safe-area-inset-bottom, 0px)), var(--sab, 0px), var(--app-sab-min, 0px))'

/** Phone record sheet and sticky filter chrome. */
export const SHEET_MAX_WIDTH = 560
export const STICKY_FILTER_HEIGHT = 56
export const GRID_MIN_WIDTH = 720

export const LIGHT_CHROME = '#5C6F8F'
export const DARK_CHROME = '#1B2433'
export const CHROME_CONTRAST = '#F4F1EB'
export const DARK_ACCENT = '#8A9BB8'

export const DARK_PAGE_BG = '#121212'
export const DARK_PAPER = '#1e1e1e'
export const DARK_SUBTLE = '#2a3140'
export const DARK_TEXT_PRIMARY = '#F4F1EB'
export const DARK_TEXT_SECONDARY = '#c5cdd6'

export type StatusTone = 'new' | 'inProgress' | 'waiting' | 'success' | 'danger' | 'info' | 'warning' | 'purple' | 'neutral'

export const LIGHT_STATUS: Record<StatusTone, { bg: string; fg: string }> = {
  new: { bg: '#e3f2fd', fg: '#0d47a1' },
  inProgress: { bg: '#fff3e0', fg: '#e65100' },
  waiting: { bg: '#fff8e1', fg: '#f57f17' },
  success: { bg: '#e8f5e9', fg: '#1b5e20' },
  danger: { bg: '#ffebee', fg: '#b71c1c' },
  info: { bg: '#e3f2fd', fg: '#1565c0' },
  warning: { bg: '#fff3e0', fg: '#e65100' },
  purple: { bg: '#f3e5f5', fg: '#6a1b9a' },
  neutral: { bg: '#eceff1', fg: '#37474f' },
} as const

export const DARK_STATUS: Record<StatusTone, { bg: string; fg: string }> = {
  new: { bg: '#0d2b4a', fg: '#90caf9' },
  inProgress: { bg: '#4e2600', fg: '#ffb74d' },
  waiting: { bg: '#3e2f00', fg: '#ffe082' },
  success: { bg: '#1b3a1d', fg: '#a5d6a7' },
  danger: { bg: '#4a1414', fg: '#ef9a9a' },
  info: { bg: '#0d2b4a', fg: '#90caf9' },
  warning: { bg: '#4e2600', fg: '#ffb74d' },
  purple: { bg: '#311246', fg: '#ce93d8' },
  neutral: { bg: '#2c3338', fg: '#cfd8dc' },
}

export const GRAYBOOK_GOLD = '#d3af37'
export const GRAYBOOK_GOLD_HOVER = '#c4a22f'

export const PALETTE_TOKENS = {
  brandNavy: BRAND_NAVY,
  brandNavyLight: BRAND_NAVY_LIGHT,
  cream: CREAM,
  paper: PAPER,
  pageBg: PAGE_BG,
  textSecondary: TEXT_SECONDARY,
  lightChrome: LIGHT_CHROME,
  darkChrome: DARK_CHROME,
  chromeContrast: CHROME_CONTRAST,
  darkAccent: DARK_ACCENT,
  darkPageBg: DARK_PAGE_BG,
  darkPaper: DARK_PAPER,
  darkSubtle: DARK_SUBTLE,
  darkTextPrimary: DARK_TEXT_PRIMARY,
  darkTextSecondary: DARK_TEXT_SECONDARY,
} as const
